import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { uploadDocument, type UploadResult } from '../lib/upload.js';

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/heic': ['.heic'],
  'image/webp': ['.webp'],
};

const MAX_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

interface FileState {
  file: File;
  status: 'idle' | 'uploading' | 'done' | 'error';
  progress: number;
  result?: UploadResult;
  error?: string;
}

interface DocumentUploaderProps {
  claimId: string;
  onUploaded?: (result: UploadResult, file: File) => void;
}

export function DocumentUploader({ claimId, onUploaded }: DocumentUploaderProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileState[]>([]);

  const updateFile = (name: string, patch: Partial<FileState>) => {
    setFiles((prev) => prev.map((f) => (f.file.name === name ? { ...f, ...patch } : f)));
  };

  const onDrop = useCallback(
    async (accepted: File[]) => {
      const newFiles: FileState[] = accepted.map((f) => ({
        file: f,
        status: 'idle',
        progress: 0,
      }));
      setFiles((prev) => [...prev, ...newFiles]);

      for (const { file } of newFiles) {
        updateFile(file.name, { status: 'uploading' });
        try {
          const result = await uploadDocument(file, claimId, (pct) => {
            updateFile(file.name, { progress: pct });
          });
          updateFile(file.name, { status: 'done', result });
          onUploaded?.(result, file);
        } catch (err) {
          updateFile(file.name, {
            status: 'error',
            error: err instanceof Error ? err.message : 'Upload failed',
          });
        }
      }
    },
    [claimId, onUploaded],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_SIZE_BYTES,
    multiple: true,
  });

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-copper bg-copper-soft'
            : 'border-hairline hover:border-copper/60 bg-bone'
        }`}
      >
        <input {...getInputProps()} />
        <div className="space-y-2">
          <div className="text-2xl text-slate-400">↑</div>
          <p className="text-sm font-medium text-ink">
            {isDragActive ? t('extraction.dropHere') : t('extraction.dropHereOrClick')}
          </p>
          <p className="text-xs text-slate-400 font-mono">{t('extraction.acceptedFormats')}</p>
        </div>
      </div>

      {fileRejections.length > 0 && (
        <div className="text-xs text-danger font-mono">
          {fileRejections.map(({ file, errors }) => (
            <div key={file.name}>
              {file.name}: {errors.map((e) => e.message).join(', ')}
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map(({ file, status, progress, error }) => (
            <div key={file.name} className="bg-bone border border-hairline rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-ink truncate max-w-[70%]">{file.name}</span>
                <StatusBadge status={status} />
              </div>

              {status === 'uploading' && (
                <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                  <div
                    className="bg-copper h-1.5 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}

              {status === 'error' && (
                <p className="text-xs text-danger font-mono mt-1">{error}</p>
              )}

              <p className="text-xs text-slate-400 font-mono mt-0.5">
                {(file.size / 1024).toFixed(1)} KB · {file.type || 'unknown type'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: FileState['status'] }) {
  const { t } = useTranslation();
  const map: Record<FileState['status'], { label: string; cls: string }> = {
    idle: { label: '—', cls: 'text-slate-400' },
    uploading: { label: t('extraction.uploading'), cls: 'text-copper font-mono' },
    done: { label: '✓', cls: 'text-success font-mono font-bold' },
    error: { label: '✗', cls: 'text-danger font-mono font-bold' },
  };
  const { label, cls } = map[status];
  return <span className={`text-xs ${cls}`}>{label}</span>;
}
